import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import './AccountPage.css';
import config from '../config';
import { getPlanDisplayName, calculateDaysRemaining } from '../utils/subscriptionUtils';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Link } from 'react-router-dom';

const API_BASE_URL = config.backendUrl;

function AccountPage({ 
  user, 
  unityAuthData, 
  cloudSaveData,
  onEditUsername,
  unityUsername,
  cleanedUsername,
  isEditingUsername,
  setIsEditingUsername,
  handleSaveUsername,
  canMakePayments
}) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  
  // Track if account info has been loaded
  const [accountInfo, setAccountInfo] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({ status: 'LOADING' });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  
  // Use the shared subscription context
  const { subscriptionData, loading: subLoading, error: subError, refreshSubscription } = useSubscription();
  
  // Update the subscription status from the shared context
  useEffect(() => {
    if (subLoading) {
      setSubscriptionStatus({ status: 'LOADING' });
      setSubscriptionLoading(true);
    } else if (subError) {
      setSubscriptionStatus({ status: 'ERROR', error: subError });
      setSubscriptionError(subError);
      setSubscriptionLoading(false);
    } else if (subscriptionData) {
      setSubscriptionStatus({
        status: subscriptionData.status,
        plan: subscriptionData.plan,
        displayName: getPlanDisplayName(subscriptionData.plan),
        expiresAt: subscriptionData.expiresAt,
        daysRemaining: calculateDaysRemaining(subscriptionData.expiresAt)
      });
      setSubscriptionLoading(false);
    } else {
      setSubscriptionStatus({ status: 'NONE' });
      setSubscriptionLoading(false);
    }
  }, [subscriptionData, subLoading, subError]);
  
  // Get account info when user or unityAuthData changes
  useEffect(() => {
    if (user) {
      setAccountInfo({
        displayName: user.displayName || unityUsername,
        email: user.email,
        photoURL: user.photoURL,
        providerId: user.providerData?.[0]?.providerId,
        createdAt: user.metadata?.creationTime,
      });
    }
  }, [user, unityAuthData, cloudSaveData, unityUsername]);

  const getAuthHeaders = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    let firebaseToken = '';

    if (currentUser) {
      try {
        firebaseToken = await currentUser.getIdToken();
      } catch (error) {
        console.error('Error getting Firebase token:', error);
      }
    }

    const headers = {
      'Content-Type': 'application/json',
    };

    if (firebaseToken) {
      headers['Authorization'] = `Bearer ${firebaseToken}`;
    }

    if (unityAuthData?.accessToken) {
      headers['X-Unity-Token'] = unityAuthData.accessToken;
    }

    return headers;
  };

  const getProviderName = (providerId) => {
    switch (providerId) {
      case 'google.com': return 'Google';
      case 'apple.com': return 'Apple';
      case 'password': return 'Email';
      default: return providerId;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getUserInitials = () => {
    if (accountInfo?.displayName) {
      const nameParts = accountInfo.displayName.split(' ');
      return nameParts.length >= 2 
        ? (nameParts[0][0] + nameParts[1][0]).toUpperCase()
        : accountInfo.displayName[0].toUpperCase();
    }
    return accountInfo?.email ? accountInfo.email[0].toUpperCase() : '?';
  };

  const handleDeleteAccount = async () => {
    if (!unityAuthData?.playerId) {
      setDeleteError('Authentication data is missing. Please try logging in again.');
      return;
    }

    try {
      setDeleteError(null);
      const headers = await getAuthHeaders();

      if (!headers['Authorization']) {
        setDeleteError('Firebase authentication is required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/users/${unityAuthData.playerId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText ? JSON.parse(errorText).error || errorText : 'Failed to delete account');
      }

      setDeleteSuccess(true);
      setIsConfirmingDelete(false);

      const { auth } = require('../firebase');
      const { signOut } = require('firebase/auth');

      await signOut(auth);
      localStorage.removeItem('unityAuthData');
      localStorage.removeItem('redirectUrl');

      setTimeout(() => {
        window.location.replace('/auth?action=logout');
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError(error.message);
    }
  };

  if (subscriptionLoading) {
    return (
      <div className="account-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your account information...</p>
        </div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="account-page">
        <div className="error-container">
          <h3>Something went wrong</h3>
          <p>{subscriptionError}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-header">
        <h2>My Account</h2>
        <p className="account-description">Manage your account settings and preferences</p>
      </div>
      
      <div className="account-sections">
        <section className="account-section profile-section">
          <h3>Profile Information</h3>
          <div className="profile-content">
            <div className="profile-photo-container">
              {accountInfo?.photoURL ? (
                <img src={accountInfo.photoURL} alt="Profile" className="profile-photo" />
              ) : (
                <div className="profile-photo-placeholder">
                  {getUserInitials()}
                </div>
              )}
            </div>
            
            <div className="profile-details">
              <div className="profile-field">
                <div className="field-label">Username</div>
                {isEditingUsername ? (
                  <div className="edit-field">
                    <input
                      type="text"
                      value={cleanedUsername || ''}
                      onChange={(e) => onEditUsername(e.target.value)}
                      className="edit-input"
                      placeholder="Enter username"
                    />
                    <div className="edit-actions">
                      <button onClick={handleSaveUsername} className="save-button">Save</button>
                      <button onClick={() => setIsEditingUsername(false)} className="cancel-button">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="field-value">
                    <span>{unityUsername || 'Set username'}</span>
                    <button onClick={() => setIsEditingUsername(true)} className="edit-button">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              
              <div className="profile-field">
                <div className="field-label">Email</div>
                <div className="field-value">{accountInfo?.email || 'N/A'}</div>
              </div>
              
              <div className="profile-field">
                <div className="field-label">Sign-in Method</div>
                <div className="field-value">{getProviderName(accountInfo?.providerId) || 'N/A'}</div>
              </div>
              
              <div className="profile-field">
                <div className="field-label">Account Created</div>
                <div className="field-value">{formatDate(accountInfo?.createdAt)}</div>
              </div>
              
              <div className="profile-field">
                <div className="field-label">Account Type</div>
                <div className="field-value">
                  {subscriptionStatus?.status === 'LOADING' ? (
                    <span className="loading-text">Loading...</span>
                  ) : subscriptionStatus?.status === 'ERROR' ? (
                    <span className="error-text">Error loading subscription</span>
                  ) : (
                    <div className="subscription-info">
                      {(!subscriptionStatus || subscriptionStatus?.status === 'NONE') ? (
                        <span className="free-badge">Free</span>
                      ) : subscriptionStatus?.status === 'CANCELLED' ? (
                        <>
                          <span className="pro-badge">Pro</span>
                          {subscriptionStatus?.plan === 'MONTHLY' ? ' (Monthly)' : 
                           subscriptionStatus?.plan === 'SIX_MONTH' ? ' (6-Month)' : 
                           subscriptionStatus?.plan === 'ANNUAL' ? ' (Annual)' : ''}
                          {subscriptionStatus?.expiresAt && (
                            <span className="expiry-notice">
                              {' - '}
                              {subscriptionStatus.daysRemaining === 0 ? 'Expires today' : 
                               subscriptionStatus.daysRemaining === 1 ? 'Expires tomorrow' :
                               `Expires in ${subscriptionStatus.daysRemaining} days`}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="pro-badge">Pro</span>
                          {subscriptionStatus?.plan === 'MONTHLY' ? ' (Monthly)' : 
                           subscriptionStatus?.plan === 'SIX_MONTH' ? ' (6-Month)' : 
                           subscriptionStatus?.plan === 'ANNUAL' ? ' (Annual)' : ''}
                        </>
                      )}
                      {canMakePayments && (
                        <Link to="/subscription" className="manage-subscription-link">
                          {(!subscriptionStatus || subscriptionStatus?.status === 'NONE') ? 'Upgrade' : 'Manage'}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="delete-account-button-container">
            <button 
              className="request-delete-account-button"
              onClick={() => setDeleteModalOpen(true)}
            >
              Request Account Deletion
            </button>
          </div>
        </section>
      </div>
      
      {deleteModalOpen && (
        <div className="modal-overlay" onClick={() => !isConfirmingDelete && setDeleteModalOpen(false)}>
          <div className="modal-content delete-account-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Account</h3>
              <button 
                className="modal-close-button"
                onClick={() => !isConfirmingDelete && setDeleteModalOpen(false)}
              >
                &times;
              </button>
            </div>
            
            {deleteSuccess ? (
              <div className="success-message">
                <p>Your account has been deleted successfully. You will be redirected to the login page shortly.</p>
              </div>
            ) : isConfirmingDelete ? (
              <div className="delete-confirmation">
                <h4>Are you sure?</h4>
                <p>This action cannot be undone. All your data, including designs, preferences, and subscription information will be permanently deleted.</p>
                
                {deleteError && (
                  <div className="error-message">
                    <p>{deleteError}</p>
                  </div>
                )}
                
                <div className="confirmation-buttons">
                  <button className="cancel-button" onClick={() => setIsConfirmingDelete(false)}>
                    Cancel
                  </button>
                  <button className="delete-button" onClick={handleDeleteAccount}>
                    Yes, Delete My Account
                  </button>
                </div>
              </div>
            ) : (
              <div className="delete-account-container">
                <p>Once you delete your account, there is no going back. Please be certain.</p>
                <button className="delete-button" onClick={() => setIsConfirmingDelete(true)}>
                  Delete Account
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;